import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  // Forgot password mode
  const [mode, setMode] = useState<'login' | 'forgot' | 'reset'>('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [showChangelog, setShowChangelog] = useState(() => {
    // Show once per version — dismissed per localStorage key
    return localStorage.getItem('lemai_changelog_seen_v2_4_1') !== '1';
  });

  // Pick up ?reset_token= from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setMode('reset');
    }
  }, [location.search]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_totp) {
        // TOTP handled inline — don't navigate yet
        return;
      }
      const role = (result as any)?.user?.role || '';
      if (role === 'contractor') { navigate('/contractor'); return; }
      // First-time users see the onboarding wizard
      const onboarded = localStorage.getItem('lemai_onboarded') === '1';
      navigate(onboarded ? '/dashboard' : '/onboarding');
    } catch (err: any) {
      setError(err?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotSent(true);
    } catch {
      setForgotSent(true); // Always show success to prevent enumeration
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (newPassword !== newPassword2) {
      setError('Passwords do not match.'); return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reset failed');
      setResetDone(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'inherit', position: 'relative',
    }}>




      {/* Version tag + What's New */}
      <div style={{
        position: 'absolute', bottom: '20px', right: '24px',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <button onClick={() => setShowChangelog(true)}
          style={{ background: 'none', border: 'none', padding: 0, fontSize: '10px', color: 'rgba(59,130,246,0.5)', fontFamily: 'monospace', cursor: 'pointer', letterSpacing: '0.5px', transition: 'color 0.15s' }}
          onMouseOver={e => (e.currentTarget as HTMLElement).style.color = 'rgba(59,130,246,0.9)'}
          onMouseOut={e => (e.currentTarget as HTMLElement).style.color = 'rgba(59,130,246,0.5)'}>
          What's New ▸
        </button>
        <span style={{ fontSize: '10px', color: '#222222', fontFamily: 'JetBrains Mono,monospace', letterSpacing: '0.05em' }}>
          LEMAI v2.4.1 · BUILD 2026.04
        </span>
      </div>

      {/* Card */}
      <div style={{
        width: '380px', maxWidth: '95vw',
        background: '#111111',
        border: '1px solid #333333',
        borderRadius: 0,
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{height:'2px',background:'#3b82f6'}} />

        {/* Header */}
        <div style={{ padding: '32px 40px 24px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: '18px', fontWeight: 700, color: '#3b82f6', letterSpacing: '0.3em', marginBottom: '8px' }}>
            LEMAI
          </div>
          <div style={{ fontSize: '9px', fontFamily: 'JetBrains Mono,monospace', fontWeight: 500, color: '#444444', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
            SECURITY OPERATIONS PLATFORM
          </div>
          <div style={{ height: '1px', background: '#222', margin: '16px 0' }} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '0 40px 36px' }}>
          {/* Email */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '10px', fontFamily: 'JetBrains Mono,monospace', fontWeight: 600, color: '#555', marginBottom: '4px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              EMAIL ADDRESS
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              autoFocus required placeholder="operator@yourorg.com"
              style={{
                width: '100%', padding: '5px 10px', height: '26px',
                background: '#1a1a1a',
                border: `1px solid ${error ? '#dc2626' : '#333'}`,
                color: '#e5e5e5', fontSize: '11px', outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(59,130,246,0.5)'; (e.target as HTMLInputElement).style.background = 'rgba(255,255,255,0.07)'; }}
              onBlur={e => { (e.target as HTMLInputElement).style.borderColor = error ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'; (e.target as HTMLInputElement).style.background = 'rgba(255,255,255,0.05)'; }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '22px' }}>
            <label style={{ display: 'block', fontSize: '10px', fontFamily: 'JetBrains Mono,monospace', fontWeight: 600, color: '#555', marginBottom: '4px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              PASSWORD
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="••••••••••"
                style={{
                  width: '100%', padding: '11px 40px 11px 14px', borderRadius: '10px',
                  background: 'rgba(255,255,255,0.05)',
                  border: `1px solid ${error ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  color: '#e2e8f0', fontSize: '14px', outline: 'none',
                  transition: 'border-color 0.15s, background 0.15s',
                  boxSizing: 'border-box',
                }}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(59,130,246,0.5)'; (e.target as HTMLInputElement).style.background = 'rgba(255,255,255,0.07)'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = error ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'; (e.target as HTMLInputElement).style.background = 'rgba(255,255,255,0.05)'; }}
              />
              <button type="button" onClick={() => setShowPass(s => !s)} style={{
                position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: '#555555', cursor: 'pointer', fontSize: '14px', padding: '2px',
              }}>
                {showPass ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          {error && (
            <div style={{
              padding: '6px 10px', borderRadius: 0, marginBottom: '12px',
              background: '#450a0a', border: '1px solid #dc2626',
              fontSize: '11px', color: '#fca5a5',
            }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={{
            width: '100%', height: '28px', borderRadius: 0,
            border: '1px solid #3b82f6',
            background: '#1e3a5f',
            color: '#3b82f6', fontSize: '10px', fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            letterSpacing: '0.08em',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}>
            {loading ? (
              <>
                <div style={{ width: '14px', height: '14px', border: '2px solid rgba(8,13,26,0.3)', borderTop: '2px solid #0a0a0a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                AUTHENTICATING...
              </>
            ) : (
              'ACCESS SYSTEM'
            )}
          </button>

          {/* Forgot password link */}
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button type="button" onClick={() => { setMode('forgot'); setError(''); setForgotSent(false); setForgotEmail(email); }}
              style={{ background: 'none', border: 'none', color: '#555', fontSize: '10px', cursor: 'pointer', padding: '4px' }}
              onMouseOver={e => (e.currentTarget as HTMLElement).style.color = '#94a3b8'}
              onMouseOut={e => (e.currentTarget as HTMLElement).style.color = '#555555'}>
              Forgot your password?
            </button>
          </div>
        </form>
      </div>

      {/* ── Forgot Password Overlay ── */}
      {mode === 'forgot' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '380px', maxWidth: '95vw', background: '#111111', border: '1px solid #333333', borderRadius: 0, overflow: 'hidden', position: 'relative' }}>
            <div style={{ height: '2px', background: '#3b82f6' }} />
            <div style={{ padding: '28px 28px 24px' }}>
              <div style={{ fontSize: '17px', fontWeight: 700, color: '#f1f5f9', marginBottom: '6px' }}>Reset Password</div>
              <div style={{ fontSize: '12px', color: '#555555', marginBottom: '20px' }}>Enter your email address and we'll send you a reset link.</div>

              {forgotSent ? (
                <div style={{ padding: '16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: '8px', fontSize: '13px', color: '#86efac', lineHeight: 1.6 }}>
                  If that email is registered, a reset link has been sent. Check your inbox (and spam folder).
                </div>
              ) : (
                <form onSubmit={handleForgot}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555555', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email Address</label>
                  <input
                    type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                    autoFocus required placeholder="operator@yourorg.com"
                    style={{ width: '100%', height: '26px', padding: '0 10px', borderRadius: 0, background: '#1a1a1a', border: '1px solid #333', color: '#e5e5e5', fontSize: '11px', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" onClick={() => setMode('login')} style={{ flex: 1, height: '28px', padding: '0 10px', borderRadius: 0, border: '1px solid #333', background: '#1a1a1a', color: '#888', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                    <button type="submit" disabled={loading} style={{ flex: 2, height: '28px', padding: '0 10px', borderRadius: 0, border: '1px solid #3b82f6', background: loading ? '#111' : '#1e3a5f', color: '#3b82f6', fontSize: '10px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                      {loading ? 'Sending…' : 'Send Reset Link'}
                    </button>
                  </div>
                </form>
              )}

              {forgotSent && (
                <button onClick={() => setMode('login')} style={{ width: '100%', marginTop: '10px', height: '28px', padding: '0 10px', borderRadius: 0, border: '1px solid #333', background: '#1a1a1a', color: '#888', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>
                  Back to Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Reset Password Overlay ── */}
      {mode === 'reset' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '380px', maxWidth: '95vw', background: '#111111', border: '1px solid #333333', borderRadius: 0, overflow: 'hidden' }}>
            <div style={{ height: '2px', background: '#3b82f6' }} />
            <div style={{ padding: '28px 28px 24px' }}>
              <div style={{ fontSize: '17px', fontWeight: 700, color: '#f1f5f9', marginBottom: '6px' }}>Set New Password</div>
              <div style={{ fontSize: '12px', color: '#555555', marginBottom: '20px' }}>Choose a strong password for your account.</div>

              {resetDone ? (
                <>
                  <div style={{ padding: '16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: '8px', fontSize: '13px', color: '#86efac', lineHeight: 1.6, marginBottom: '16px' }}>
                    Password updated successfully. You can now sign in with your new password.
                  </div>
                  <button onClick={() => { setMode('login'); setError(''); }} style={{ width: '100%', height: '28px', padding: '0 10px', borderRadius: 0, border: '1px solid #3b82f6', background: '#1e3a5f', color: '#3b82f6', fontSize: '10px', fontWeight: 700, cursor: 'pointer' }}>
                    Sign In
                  </button>
                </>
              ) : (
                <form onSubmit={handleReset}>
                  {error && (
                    <div style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', fontSize: '12px', color: '#fca5a5' }}>{error}</div>
                  )}
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555555', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>New Password</label>
                  <div style={{ position: 'relative', marginBottom: '12px' }}>
                    <input
                      type={showNewPass ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                      autoFocus required placeholder="Min 8 chars, upper, lower, number"
                      style={{ width: '100%', padding: '11px 40px 11px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                    />
                    <button type="button" onClick={() => setShowNewPass(s => !s)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#555555', cursor: 'pointer', fontSize: '14px', padding: '2px' }}>{showNewPass ? '🙈' : '👁'}</button>
                  </div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555555', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Confirm Password</label>
                  <input
                    type="password" value={newPassword2} onChange={e => setNewPassword2(e.target.value)}
                    required placeholder="Re-enter password"
                    style={{ width: '100%', padding: '11px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${newPassword2 && newPassword2 !== newPassword ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)'}`, color: '#e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '18px' }}
                  />
                  <button type="submit" disabled={loading} style={{ width: '100%', padding: '11px', borderRadius: '8px', border: 'none', background: loading ? 'rgba(59,130,246,0.4)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', fontSize: '13px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                    {loading ? 'Saving…' : 'Set New Password'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Changelog Modal ── */}
      {showChangelog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '460px', maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto', background: 'rgba(13,19,33,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #3b82f6 30%, #6366f1 60%, transparent)', flexShrink: 0 }} />
            <div style={{ padding: '28px', overflowY: 'auto', maxHeight: 'calc(80vh - 2px)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <div style={{ fontSize: '17px', fontWeight: 700, color: '#f1f5f9' }}>What's New in Lemai</div>
                  <div style={{ fontSize: '11px', color: '#555555', marginTop: '3px' }}>v2.4.1 · April 2026</div>
                </div>
                <button onClick={() => { setShowChangelog(false); localStorage.setItem('lemai_changelog_seen_v2_4_1', '1'); }}
                  style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '8px', color: '#555555', width: '30px', height: '30px', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>✕</button>
              </div>

              {[
                { version: 'v2.4.1', date: 'Apr 2026', tag: 'NEW', tagColor: '#22c55e', items: [
                  { icon: '🔐', title: 'Password Reset', desc: 'Operators can now self-serve password resets via email link.' },
                  { icon: '✉️', title: 'SMTP Email Configuration', desc: 'Configure outbound email (host, port, credentials) directly in Settings → Email.' },
                  { icon: '📋', title: 'Audit Log CSV Export', desc: 'Download the full audit trail as a filterable CSV from Settings → Email.' },
                  { icon: '🔔', title: 'Browser Push Notifications', desc: 'Critical and high-severity alarms trigger OS-level push notifications even when tab is in background.' },
                  { icon: '🏷️', title: 'Live Alarm Count Badge', desc: 'The SOC navigation item now shows a live red badge with active alarm count.' },
                  { icon: '🖥️', title: 'Kiosk / Fullscreen Mode', desc: 'SOC Dashboard now has a one-click fullscreen kiosk mode for wall-mounted displays.' },
                  { icon: '📧', title: 'Shift Handover Emails', desc: 'Handover reports are now automatically emailed to the incoming officer.' },
                  { icon: '🔒', title: 'Two-Factor Authentication', desc: 'TOTP-based 2FA can now be configured per-operator in Settings → Security.' },
                  { icon: '🤝', title: 'User Invitation Emails', desc: 'New operators receive a welcome email with credentials when added by an admin.' },
                ] },
                { version: 'v2.3.0', date: 'Feb 2026', tag: 'PREV', tagColor: '#555555', items: [
                  { icon: '🗺️', title: 'Site Map View', desc: 'Visual site map with controller status overlay in SOC Dashboard.' },
                  { icon: '📊', title: 'SLA Reporting', desc: 'Response time SLA tracking with breach alerts and monthly reports.' },
                  { icon: '🚶', title: 'Tour Management', desc: 'Guard tour scheduling, check-point scanning, and completion reports.' },
                ] },
              ].map(section => (
                <div key={section.version} style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#e2e8f0' }}>{section.version}</span>
                    <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: `${section.tagColor}22`, color: section.tagColor, border: `1px solid ${section.tagColor}44`, letterSpacing: '0.5px' }}>{section.tag}</span>
                    <span style={{ fontSize: '10px', color: '#222222', marginLeft: '2px' }}>{section.date}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {section.items.map((item, i) => (
                      <div key={i} style={{ display: 'flex', gap: '10px', padding: '10px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <span style={{ fontSize: '16px', flexShrink: 0, lineHeight: '20px' }}>{item.icon}</span>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>{item.title}</div>
                          <div style={{ fontSize: '11px', color: '#555555', marginTop: '2px', lineHeight: 1.5 }}>{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <button onClick={() => { setShowChangelog(false); localStorage.setItem('lemai_changelog_seen_v2_4_1', '1'); }}
                style={{ width: '100%', padding: '11px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', fontSize: '13px', fontWeight: 700, cursor: 'pointer', marginTop: '4px' }}>
                Got it — Let me sign in
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(255,255,255,0.2) !important; }
        input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px rgba(13,19,33,0.95) inset !important; -webkit-text-fill-color: #e2e8f0 !important; }
      `}</style>
    </div>
  );
}
